import { Navigate, Outlet } from "react-router-dom";
import { BillingGate } from "@/components/BillingGate";
import Sidebar from "@/components/Sidebar";
import Topbar from "@/components/Topbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Clock, ShieldX, LogOut, UserCog } from "lucide-react";

export default function Layout() {
  const { user, loading, logout, setUser } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background bg-grid">
        <div className="h-14 w-14 flex items-center justify-center animate-pulse-gold">
          <img src="/streamworship-mark.png" alt="StreamWorship" className="h-14 w-14 object-contain" />
        </div>
        <p className="mt-6 text-gray-400 text-sm">Loading control panel…</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  // Pending / rejected accounts cannot access the portal (admins always can, and
  // an admin impersonating a member can view their account regardless of status).
  if (user.role !== "admin" && user.status !== "approved" && !user.impersonating) {
    return <PendingScreen user={user} onLogout={logout} />;
  }

  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar />
        <ImpersonationBanner user={user} setUser={setUser} />
        <BillingGate user={user}>
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </BillingGate>
      </div>
    </div>
  );
}

function ImpersonationBanner({ user, setUser }) {
  if (!user.impersonating) return null;
  const exit = async () => {
    try {
      const res = await api.post("/auth/stop-impersonation");
      if (setUser) setUser(res.data);
      window.location.href = "/team";
    } catch (e) {
      window.location.href = "/team";
    }
  };
  return (
    <div data-testid="impersonation-banner" className="flex items-center gap-3 px-6 py-2.5 text-sm border-b bg-blue-500/10 border-blue-500/30 text-blue-100">
      <UserCog className="h-4 w-4 shrink-0 text-blue-300" />
      <p className="min-w-0 truncate">
        Admin view — you're viewing <span className="font-semibold">{user.name || user.email}</span>'s account.
      </p>
      <button
        data-testid="exit-impersonation"
        onClick={exit}
        className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border border-blue-400/40 text-blue-100 hover:bg-blue-500/20 transition-all active:scale-[0.98]"
      >
        <LogOut className="h-3.5 w-3.5" /> Exit to admin
      </button>
    </div>
  );
}

function PendingScreen({ user, onLogout }) {
  const rejected = user.status === "rejected";
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background bg-grid p-6 text-center">
      <div className={"h-16 w-16 rounded-2xl flex items-center justify-center " + (rejected ? "bg-red-500/10 border border-red-500/30" : "bg-amber-500/10 border border-amber-500/30")}>
        {rejected ? <ShieldX className="h-8 w-8 text-red-400" /> : <Clock className="h-8 w-8 text-amber-400 animate-pulse-gold" />}
      </div>
      <h1 className="mt-6 font-display text-2xl font-bold text-white" data-testid="pending-screen-title">
        {rejected ? "Account not approved" : "Your account is pending approval"}
      </h1>
      <p className="mt-3 text-gray-400 max-w-md leading-relaxed">
        {rejected
          ? "Your access request was not approved. If you believe this is a mistake, please contact your administrator."
          : `Thanks for registering${user.organization ? `, ${user.organization}` : ""}! An administrator will review and activate your account shortly. You'll get full access once approved.`}
      </p>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-gray-800 bg-card px-4 py-1.5 text-sm">
        <span className="text-gray-500">Signed in as</span>
        <span className="text-white font-medium">{user.email}</span>
      </div>
      <button data-testid="pending-logout" onClick={onLogout} className="mt-8 px-5 py-2.5 rounded-xl border border-gray-700 text-gray-300 hover:border-amber-500/40 hover:text-white transition-colors flex items-center gap-2">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );
}
